<template>
  <div class="text-center">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      bottom
      @update:model-value="closeMenu(false)"
    >
      <template v-slot:activator="{ props }">
        <v-chip :color="readableName() ? 'primary' : 'primary'" v-bind="props" label>
          {{ $t("common.action.created") }}
          <span v-if="readableName()">&nbsp;|&nbsp;</span>
          <span class="font-weight-bold"> {{ readableName() }}</span>
          <v-btn
            v-if="readableName()"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset(true)"
          />
        </v-chip>
      </template>

      <v-card min-width="350" class="mt-2" v-click-outside="handleClickOutside">
        <v-list>
          <v-list-item :title="$t('page.payment.filter.filterByCreationDate')">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="handleCloseMenu" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-list class="mt-1">
          <v-row class="mx-4" no-gutters>
            <v-btn variant="text" size="x-small" color="primary" @click="reset(false)">{{
              $t("common.action.reset")
            }}</v-btn></v-row
          >

          <v-row class="mx-6 mt-3" no-gutters>
            <v-col cols="4"> <v-list-item :subtitle="$t('page.payment.filter.startDate')" /></v-col>
            <v-col cols="8">
              <date-picker
                :date="startDate"
                @update-date="updateStartDate"
                @clear-date="clearStartDate"
            /></v-col>
          </v-row>

          <v-row class="mx-6 mt-3" no-gutters>
            <v-col cols="4"> <v-list-item :subtitle="$t('page.payment.filter.endDate')" /></v-col>
            <v-col cols="8">
              <date-picker :date="endDate" @update-date="updateEndDate" @clear-date="clearEndDate"
            /></v-col>
          </v-row>
        </v-list>

        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="
              closeMenu(true);
              updateFilterPayments();
            "
            color="primary"
            class="mr-2"
          >
            {{ $t("page.payment.filter.applyFilter") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import { usePaymentStore } from "@/payments/stores/payments";
import { useInventoryLogStore } from "@/catalog/stores/inventorylog";
import i18n from "@/app/plugins/i18n";
import { getUnixTime, stringDateToStringWithNoTime } from "@/shared/utils/date.utils";
import DatePicker from "@/shared/components/Common/DatePicker.vue";

// Define props to specify which store to use
const props = defineProps({
  storeType: {
    type: String,
    default: "payment",
    validator: (value: string) => ["payment", "inventoryLog"].includes(value),
  },
});

const menu = ref(false);
const startDate = ref();
const endDate = ref();
const applyingFilters = ref(false);

// Get the appropriate store based on the storeType prop
const paymentStore = usePaymentStore();
const inventoryLogStore = useInventoryLogStore();

// Computed properties for filtered dates
const filteredStartDate = computed(() => {
  if (props.storeType === "inventoryLog") {
    return inventoryLogStore.filters?.startDate || null;
  }
  return paymentStore.filterPaymentCondition?.startDate || null;
});

const filteredEndDate = computed(() => {
  if (props.storeType === "inventoryLog") {
    return inventoryLogStore.filters?.endDate || null;
  }
  return paymentStore.filterPaymentCondition?.endDate || null;
});

const reset = async (clear?: boolean) => {
  startDate.value = [];
  endDate.value = [];

  if (props.storeType === "inventoryLog") {
    if (clear) {
      inventoryLogStore.setFilter("startDate", null);
      inventoryLogStore.setFilter("endDate", null);
    }
  } else {
    const filterPaymentCondition = { ...paymentStore.filterPaymentCondition };
    delete filterPaymentCondition.startDate;
    delete filterPaymentCondition.endDate;
    if (clear) {
      await paymentStore.getFilteredPayments();
    }
    paymentStore.setFilterPaymentCondition(filterPaymentCondition);
  }
};

const updateFilterPayments = () => {
  if (props.storeType === "inventoryLog") {
    // For inventory log store - Using setFilter instead of setFilters to avoid replacing all filters
    inventoryLogStore.setFilter("startDate", getUnixTime(startDate.value));
    inventoryLogStore.setFilter("endDate", getUnixTime(endDate.value) + 24 * 60 * 60 - 1);
  } else {
    // For payment store
    const oldCondition = paymentStore.filterPaymentCondition;
    const newCondition = {
      ...oldCondition,
      startDate: getUnixTime(startDate.value),
      endDate: getUnixTime(endDate.value),
    };
    paymentStore.setFilterPaymentCondition(newCondition);
    paymentStore.getFilteredPayments();
  }

  startDate.value = [];
  endDate.value = [];
  applyingFilters.value = false;
};

const updateStartDate = (value: string) => {
  startDate.value = value;
  applyingFilters.value = true;
};

const clearStartDate = () => {
  startDate.value = [];
  applyingFilters.value = true;
};

const updateEndDate = (value: string) => {
  endDate.value = value;
  applyingFilters.value = true;
};

const clearEndDate = () => {
  endDate.value = [];
  applyingFilters.value = true;
};

const closeMenu = (close: boolean) => {
  menu.value = !close;
};

const readableName = () => {
  let name = "";
  const readableStartDate = filteredStartDate.value
    ? stringDateToStringWithNoTime(filteredStartDate.value)
    : "";
  const readableEndDate = filteredEndDate.value
    ? stringDateToStringWithNoTime(filteredEndDate.value)
    : "";
  if (filteredStartDate.value && filteredEndDate.value) {
    if (filteredStartDate.value === filteredEndDate.value) {
      name = ` ${i18n.global.t("page.payment.filter.isEqualTo")} ${readableStartDate}`;
    } else if (filteredStartDate.value < filteredEndDate.value) {
      name = ` ${i18n.global.t(
        "page.payment.filter.isBetween",
      )} ${readableStartDate} and ${readableEndDate}`;
    }
  } else if (filteredStartDate.value) {
    name = ` ${i18n.global.t("page.payment.filter.isOnOrAfter")} ${readableStartDate}`;
  } else if (filteredEndDate.value) {
    name = ` ${i18n.global.t("page.payment.filter.isBeforeOrOn")} ${readableEndDate}`;
  }
  return name;
};

const handleClickOutside = (event: MouseEvent) => {
  if (!(event.target instanceof HTMLElement)) return;

  // Check if we're clicking inside the date picker
  const isDatePickerClick = event.target.closest(".dp__outer_menu_wrap");

  // Check if we're clicking inside the parent menu
  const isMenuClick = event.target.closest(".v-menu__content");

  // Only close if clicking outside both the date picker and the menu
  if (menu.value === true && !isDatePickerClick && !isMenuClick) {
    startDate.value = [];
    endDate.value = [];
    closeMenu(true);
  }
};

const handleCloseMenu = () => {
  applyingFilters.value = false;
  closeMenu(true);
};
</script>
